import { ApiTransport } from './ApiTransport';
import type { ComplianceMetrics } from './types';
import { extractError } from './apiErrors';

export class ComplianceClient {
  constructor(private readonly transport: ApiTransport) {}

  async getProjectPolicies(
    projectKey: string,
  ): Promise<Record<string, unknown>> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(projectKey)}/policies`,
    );
    if (!response.ok) return {};
    const result = await response.json();
    return result.data;
  }

  async setProjectPolicy(
    projectKey: string,
    policyKey: string,
    value: unknown,
  ): Promise<unknown> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(
        projectKey,
      )}/policies/${encodeURIComponent(policyKey)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      },
    );
    if (!response.ok) {
      const msg = await extractError(response, 'Failed to set policy');
      throw new Error(msg);
    }
    return response.json();
  }

  async getProjectCompliance(
    projectKey: string,
    days = 30,
  ): Promise<ComplianceMetrics> {
    const response = await this.transport.request(
      `/roles/projects/${encodeURIComponent(
        projectKey,
      )}/compliance?days=${days}`,
    );
    if (!response.ok) {
      await response.text().catch(() => '');
      return {
        projectKey,
        period: { days, since: new Date().toISOString() },
        activities: { total: 0, active: 0, completed: 0, errors: 0 },
        artifacts: { total: 0, approved: 0, pending: 0 },
        auditActions: 0,
        members: { total: 0, temporary: 0, expired: 0 },
        policies: {},
      };
    }
    return response.json();
  }
}
